import { z } from 'zod';
import { decimalString, optionalDecimal, optionalText } from './form-helpers';

// Mirrors the backend hedge_instrument enum (Future | FX); Option / Forward
// exist on hedge_instrument_type for requests but not on the execution side,
// so offering them here would surface dropdown values the API rejects.
const HEDGE_INSTRUMENTS = ['Future', 'FX'] as const;
export type HedgeInstrument = (typeof HEDGE_INSTRUMENTS)[number];

export const executionFormSchema = z
  .object({
    quantityMt: decimalString,
    executedPrice: decimalString,
    executedPriceCurrency: z.string().trim().length(3, 'ISO 4217 code'),
    executionDate: z.string().min(1, 'Required'),
    promptDate: optionalText(20),
    instrument: z.enum(HEDGE_INSTRUMENTS),
    exchange: z.string().trim().min(1, 'Required'),
    brokerName: z.string().trim().min(1, 'Required'),
    contractReference: optionalText(200),
    fixingPrice: optionalDecimal,
    notes: optionalText(1000),
  })
  .superRefine((value, ctx) => {
    if (!value.promptDate || value.promptDate.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['promptDate'],
        message: value.instrument === 'FX' ? 'Required for FX' : 'Required for Futures',
      });
    }
  });

export type ExecutionFormValues = z.infer<typeof executionFormSchema>;

export const PRICE_FIX_REASONS = new Set(['Price Fix', 'Fix Reversal']);

export function reasonRequiresFixingPrice(reason: string | null): boolean {
  if (reason === null) return false;
  return PRICE_FIX_REASONS.has(reason);
}

// Both instruments settle on a date and both store it in prompt_date; only the
// desk-facing name differs (LME prompt vs FX value date).
export function settlementDateLabel(instrument: HedgeInstrument): string {
  return instrument === 'FX' ? 'Value date' : 'Prompt date';
}
